import React, { FC, useEffect } from 'react'
import { useT } from '@transifex/react'
import { VendImage } from '@vend/react-utilities'
import { Button, ModalActions } from '@lightspeed/design-system-react'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { selectActiveRegisterId } from '../../../state/slices/posState/posStateSlice'
import { useFetchPairing } from '../lspay-payment-modal/hooks/useFetchPairing'
import { setAttemptPayment } from '../../../state/slices/terminalStatus/terminalStatusSlice'
import { useTerminalStatusFromRedux } from './useTerminalStatusFromRedux'
import { getWarningConfig } from './terminal-status-event-handler'

interface Props {
  setDismissible: (dismissible: boolean) => void
}

export const TerminalStatusContent: FC<Props> = ({ setDismissible }) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const registerId = useAppSelector(selectActiveRegisterId)
  const { pairing } = useFetchPairing(registerId ?? '')
  const { warningType } = useTerminalStatusFromRedux()

  const ATTEMPT_ANYWAY_BUTTON_TEXT = t('Attempt anyway', {
    _context: 'Terminal status attempt anyway button text',
  })

  useEffect(() => {
    setDismissible(true)
  }, [setDismissible])

  if (!warningType || !pairing) {
    return null
  }

  const primaryActionButtonText = ATTEMPT_ANYWAY_BUTTON_TEXT
  const onPrimaryAction = () => dispatch(setAttemptPayment(true))
  const dataTrackPrimaryAction = 'attempt-payment-terminal-status-modal'

  const warning = getWarningConfig(t, pairing.deviceLabel)[warningType]

  const hasBullets =
    warning.modalContent.bullets && warning.modalContent.bullets.length > 0

  return (
    <section className="terminal-status-modal">
      <div className="main">
        <div className="warning-icon">
          <VendImage
            alt="warning icon"
            relativeImagePath={warning.modalContent.imagePath}
            relativeDarkImagePath={warning.modalContent.darkImagePath}
          />
        </div>

        <div className="warning-content">
          <div className="title">{warning.title}</div>
          <div className="body">{warning.modalContent.message}</div>

          {hasBullets && (
            <ul className="list">
              {warning.modalContent.bullets?.map(b => (
                <li key={b} className="list-item">
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            data-track={dataTrackPrimaryAction}
            type="button"
            variant="go"
            onClick={onPrimaryAction}
          >
            {primaryActionButtonText}
          </Button>
        </div>
      </ModalActions>
    </section>
  )
}
